import axios from "axios";
const APIURL = process.env.APIURL;
const APPSECRET = process.env.APPSECRET;
const headers = {
  accept: "application/json",
  "Content-Type": "application/json; charset=utf-8",
  "X-API-KEY": APPSECRET,
  "Access-Control-Allow-Origin": "*",
};
const formHeader = {
  accept: "application/json",
  "Content-Type": "multipart/form-data",
  "X-API-KEY": APPSECRET,
  "Access-Control-Allow-Origin": "*",
};

export const postRecord = (url, data, options = {}) => {
  return new Promise((resolve, reject) => {
    // Create headers dynamically based on authentication type
    const requestHeaders = data?.Refresh_Token
      ? {
          accept: "application/json",
          "Content-Type": "application/json; charset=utf-8",
          Authorization: `Bearer ${data.Refresh_Token}`,
          "X-API-KEY": APPSECRET,
          "Access-Control-Allow-Origin": "*",
        }
      : headers; // Use existing API key headers

    // Configure axios with timeout and optimization
    const axiosConfig = {
      headers: requestHeaders,
      timeout: options.timeout || 15000, // 15 seconds default timeout
      validateStatus: (status) => status < 500, // Don't reject on 4xx errors
      ...options.axiosConfig,
    };

    axios
      .post(APIURL + url, data, axiosConfig)
      .then((res) => {
        resolve(res);
      })
      .catch((err) => {
        // Enhanced error handling
        if (err.code === "ECONNABORTED") {
          err.message = "Request timeout. Please check your connection.";
        }
        reject(err);
      });
  });
};
export const uploadRecord = (url, data) => {
  return new Promise((resolve, reject) => {
    // Create headers dynamically based on authentication type
    const requestHeaders = data?.Refresh_Token
      ? {
          accept: "application/json",
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${data.Refresh_Token}`,
          "X-API-KEY": APPSECRET,
          "Access-Control-Allow-Origin": "*",
        }
      : formHeader; // Use existing API key headers

    axios
      .post(APIURL + url, data, {
        headers: requestHeaders,
      })
      .then((res) => {
        resolve(res);
      })
      .catch((err) => reject(err));
  });
};

export const downloadBinaryRecord = (url, data) => {
  return new Promise((resolve, reject) => {
    // Create headers for binary download
    const requestHeaders = data?.Refresh_Token
      ? {
          "Content-Type": "application/json; charset=utf-8",
          Authorization: `Bearer ${data.Refresh_Token}`,
          "X-API-KEY": APPSECRET,
          "Access-Control-Allow-Origin": "*",
        }
      : {
          "Content-Type": "application/json; charset=utf-8",
          "X-API-KEY": APPSECRET,
          "Access-Control-Allow-Origin": "*",
        };

    axios
      .post(APIURL + url, data, {
        headers: requestHeaders,
        responseType: "blob", // Important: This tells axios to expect binary data
      })
      .then((res) => {
        resolve(res);
      })
      .catch((err) => reject(err));
  });
};

export const handleResponse = (response) => {
  // Handle cases where the response is undefined or null
  if (!response) {
    return {
      statusText: "ERROR",
      color: "error",
      message: "No response received. Please check your network connection.",
    };
  }

  const { status, data, statusText } = response;

  // Handle successful responses
  if (status === 200 && data) {
    if (data.result) {
      // Parse `result` if it exists
      const result =
        typeof data.result === "string" ? JSON.parse(data.result) : data.result;
      return {
        statusText: statusText || "OK",
        color: result.Color || "success",
        message: result.Message || "Operation completed successfully.",
      };
    } else if (data === "Unauthorized Access") {
      // Handle unauthorized access
      return {
        statusText: "ERROR",
        color: "error",
        message: "Unauthorized Access. Please check your credentials.",
      };
    } else if (data.errno) {
      // Handle database errors
      return {
        statusText: "ERROR",
        color: "error",
        message: `Database Error: Code ${data.errno} - ${
          data.sqlMessage || "An error occurred."
        }`,
      };
    } else if (data.Message && data.Color) {
      // General response with Message and Color
      return {
        statusText: statusText || "OK",
        color: data.Color,
        message: data.Message,
      };
    } else {
      // Unexpected data structure
      return {
        statusText: "ERROR",
        color: "error",
        message: "Unexpected data received. Please contact support.",
      };
    }
  }

  // Handle non-200 HTTP responses
  if (status >= 400 && status < 500) {
    return {
      statusText: "ERROR",
      color: "warning",
      message:
        data?.Message || "Client error occurred. Please check your request.",
    };
  }

  if (status >= 500) {
    return {
      statusText: "ERROR",
      color: "error",
      message:
        data?.Message || "Server error occurred. Please try again later.",
    };
  }

  // Handle unexpected cases
  return {
    statusText: "UNKNOWN",
    color: "error",
    message: "An unexpected error occurred. Please report this issue.",
  };
};
export const handleApiResponse = (
  response,
  setSnackOptions,
  setProcessing,
  setSnackOpen,
  resetAndPreview
) => {
  if (response.status === 200 || response.statusText === "OK") {
    const result =
      response.data?.Response_Data && JSON.parse(response.data?.Response_Data);
    setSnackOptions({
      color: result.Color || "success", // Use the color from the response or default to "success"
      message: result.Message || "Record added successfully.",
      icon: result.Icon || "CheckCircleIcon", // Use the icon from the response or default to success icon
    });
    setProcessing(false);
    setSnackOpen(true);
    resetAndPreview();
  }
};

export const handleApiError = (
  error,
  setSnackOptions,
  setProcessing,
  setSnackOpen
) => {
  if (error?.response?.data?.detail) {
    const errorDetail = error.response.data.detail;
    var errorMessages = "";
    if (Array.isArray(errorDetail)) {
      errorDetail.forEach((error, i) => {
        errorMessages += error.msg + " | " + "\n";
      });
    } else {
      errorMessages = errorDetail;
    }
    // Use error message from backend
    setSnackOptions({
      color: error.response.data?.Color || "error",
      message: errorMessages || "Something went wrong during registration.",
      icon: error.response.data?.Icon || "ErrorOutlineOutlinedIcon",
    });
  } else {
    // Handle network or unexpected errors
    setSnackOptions({
      color: "error",
      message: error.message || "Network error. Please try again.",
      icon: "ErrorOutlineOutlinedIcon",
    });
  }
  setSnackOpen(true);
  setProcessing(false); // Stop loading
};

export const getAppSettingByName = (settings, name) => {
  return settings.filter((setting) => setting.Setting_Name === name);
};
